import { useEffect, useState, useContext } from "react";
import { useChangeBooleans, useComponentProperties, useFetchedValues, useResetActions, useResetAttributeTable, useResetComponentProperties, useSaveComponent, useUpdateSyncComponentAttributes } from "../../../Hooks/Display/useComponentProperties";
import { useSelectedComponent } from "../../../Hooks/Display/useComponents";
import { usePortalPages, useSelectedPage, useUpdatePageComponent } from "../../../Hooks/Display/usePages";
import { usePropsStoreValue } from "../../../Hooks/PropStore/usePropsStore";
import ComponentToolbar from "./ComponentToolbar/ComponentToolbar";
import Scrollable from "../../Shared/Edit/Scrollable";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { Checkbox } from "@progress/kendo-react-inputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import componentPropertiesMap from "./ComponentFormProperties.json";
import { getAttributes } from "../../../API/Attribute/Attribute";
import MappingAttributeForm from "./CalendarComponents/MappingAttributes";
import EventActions from "./CalendarComponents/EventActions";
import ActionTable from "../ActionTable/ActionTable";
import AttributeTable from "../AttributeTable/AttributeTable";
import WorkWeekMultiSelect from "./CalendarComponents/WorkWeekMultiSelect";
import ComponentFormSkeleton from "../../Display/Shared/ComponentFormSkeleton";
import useInternalUserCheck from "../../../Hooks/Misc/useInternalUserCheck";
import { useTranslation } from "react-i18next";
import { GlobalContext } from "../../../Renderers/GlobalContext";

function Calendar() {

    const [DefaultViews, setDefaultViews] = useState([
        {
            "text": "Month",
            "value": "Month"
        },
        {
            "text": "Week",
            "value": "Week"
        },
        {
            "text": "Day",
            "value": "Day"
        },
        {
            "text": "Year",
            "value": "Year"
        }
    ]);

    const [Weeks, setWeeks] = useState([
        {
            "text": "Sunday",
            "value": "Sunday"
        },
        {
            "text": "Monday",
            "value": "Monday"
        },
        {
            "text": "Tuesday",
            "value": "Tuesday"
        },
        {
            "text": "Wednesday",
            "value": "Wednesday"
        },
        {
            "text": "Thursday",
            "value": "Thursday"
        },
        {
            "text": "Friday",
            "value": "Friday"
        },
        {
            "text": "Saturday",
            "value": "Saturday"
        }
    ]);
    const [Time, setTime] = useState([
        {
            "text": "12:00 AM",
            "value": "12:00 AM"
        },
        {
            "text": "01:00 AM",
            "value": "01:00 AM"
        },
        {
            "text": "02:00 AM",
            "value": "02:00 AM"
        },
        {
            "text": "03:00 AM",
            "value": "03:00 AM"
        },
        {
            "text": "04:00 AM",
            "value": "04:00 AM"
        },
        {
            "text": "05:00 AM",
            "value": "05:00 AM"
        },
        {
            "text": "06:00 AM",
            "value": "06:00 AM"
        },
        {
            "text": "07:00 AM",
            "value": "07:00 AM"
        },
        {
            "text": "08:00 AM",
            "value": "08:00 AM"
        },
        {
            "text": "09:00 AM",
            "value": "09:00 AM"
        },
        {
            "text": "10:00 AM",
            "value": "10:00 AM"
        },
        {
            "text": "11:00 AM",
            "value": "11:00 AM"
        },
        {
            "text": "12:00 PM",
            "value": "12:00 PM"
        },
        {
            "text": "01:00 PM",
            "value": "01:00 PM"
        },
        {
            "text": "02:00 PM",
            "value": "02:00 PM"
        },
        {
            "text": "03:00 PM",
            "value": "03:00 PM"
        },
        {
            "text": "04:00 PM",
            "value": "04:00 PM"
        },
        {
            "text": "05:00 PM",
            "value": "05:00 PM"
        },
        {
            "text": "06:00 PM",
            "value": "06:00 PM"
        },
        {
            "text": "07:00 PM",
            "value": "07:00 PM"
        },
        {
            "text": "08:00 PM",
            "value": "08:00 PM"
        },
        {
            "text": "09:00 PM",
            "value": "09:00 PM"
        },
        {
            "text": "10:00 PM",
            "value": "10:00 PM"
        },
        {
            "text": "11:00 PM",
            "value": "11:00 PM"
        }
    ]);

    const [slotIntervals, setSlotIntervals] = useState([
        {
            "text": "1",
            "value": "1"
        },
        {
            "text": "2",
            "value": "2"
        },
        {
            "text": "3",
            "value": "3"
        },
        {
            "text": "4",
            "value": "4"
        },
        {
            "text": "5",
            "value": "5"
        },
        {
            "text": "6",
            "value": "6"
        },
        {
            "text": "7",
            "value": "7"
        },
        {
            "text": "8",
            "value": "8"
        },
        {
            "text": "9",
            "value": "9"
        },
        {
            "text": "10",
            "value": "10"
        }
    ]);

    const [slotDurations, setSlotDurations] = useState([
        {
            "text": "1 hour",
            "value": "1 hour"
        },
        {
            "text": "1.5 hours",
            "value": "1.5 hours"
        },
        {
            "text": "2 hours",
            "value": "2 hours"
        },
        {
            "text": "2.5 hours",
            "value": "2.5 hours"
        },
        {
            "text": "3 hours",
            "value": "3 hours"
        },
        {
            "text": "3.5 hours",
            "value": "3.5 hours"
        },
        {
            "text": "4 hours",
            "value": "4 hours"
        },
        {
            "text": "4.5 hours",
            "value": "4.5 hours"
        },
        {
            "text": "5 hours",
            "value": "5 hours"
        },
        {
            "text": "5.5 hours",
            "value": "5.5 hours"
        },
        {
            "text": "6 hours",
            "value": "6 hours"
        },
        {
            "text": "6.5 hours",
            "value": "6.5 hours"
        },
        {
            "text": "7 hours",
            "value": "7 hours"
        },
        {
            "text": "7.5 hours",
            "value": "7.5 hours"
        },
        {
            "text": "8 hours",
            "value": "8 hours"
        },
        {
            "text": "8.5 hours",
            "value": "8.5 hours"
        },
        {
            "text": "9 hours",
            "value": "9 hours"
        },
        {
            "text": "9.5 hours",
            "value": "9.5 hours"
        },
        {
            "text": "10 hours",
            "value": "10 hours"
        }
    ]);

    const [TimeFormats, setTimeFormats] = useState([
        {
            "text": "12 hours",
            "value": "12 hours"
        },
        {
            "text": "24 hours",
            "value": "24 hours"
        }
    ]);

    const [WeekNumbers, setWeekNumbers] = useState([
        {
            "text": "Off",
            "value": "Off"
        },
        {
            "text": "First Day of Year",
            "value": "First Day of Year"
        },
        {
            "text": "First Full Week",
            "value": "First Full Week"
        },
        {
            "text": "First Four-Day Week",
            "value": "First Four-Day Week"
        }
    ]);

    const formProperties = componentPropertiesMap.Calendar;
    const { user } = useContext(GlobalContext).state;
    // hooks
    const portalPages = usePortalPages();
    const selectedPage = useSelectedPage();
    const selectedComponent = useSelectedComponent();
    const accessedFromDesigner = usePropsStoreValue("accessedFromDesigner");
    const entityId = usePropsStoreValue("entityId");
    const advancedMode = usePropsStoreValue("advancedMode");
    const isInternalUser = useInternalUserCheck(user.email);
    const saveComponent = useSaveComponent();
    const updateSyncedComponentAttributes = useUpdateSyncComponentAttributes();
    const updatePageComponent = useUpdatePageComponent(selectedPage.parentId, selectedPage._id);
    const resetContext = useResetComponentProperties();
    const resetActions = useResetActions();
    const resetAttributes = useResetAttributeTable();

    const [properties, setProperties] = useComponentProperties();
    const [{ generalProperties: fetchedValues }, { updateGeneralFetchedValue }] = useFetchedValues();
    const [{
        isGeneralPropertiesChanged,
        isActionTableChanged,
        isAttributeTableChanged
    }, setChangeProperty] = useChangeBooleans();

    const [loading, setLoading] = useState(true);

    const { t, ready } = useTranslation([
        "display/common",
        "display/component_toolbar",
        "display/components/calendar",
        "display/tables/attribute"
    ])

    // componentWillUnmount
    useEffect(() => {
        return () => {
            resetContext();
        }
    }, [])

    useEffect(() => {
        if (selectedPage && selectedComponent) {
            getData();
        }
    }, [])

    useEffect(() => {
        if (!loading) {
            setComponentPropertiesUsingAttributes(fetchedValues.attributes)
        }
    }, [selectedPage, selectedComponent])

    useEffect(() => {
        setIsChanged();
    }, [properties])

    useEffect(() => {
        if (ready) {
            setDefaultViews(prevValue => prevValue.map(val => ({
                text: t("default_views." + val.value.toLowerCase(), { ns: "display/components/calendar" }),
                value: val.value
            })))
            setWeeks(prevValue => prevValue.map(val => ({
                text: t("days." + val.value.toLowerCase(), { ns: "display/components/calendar" }),
                value: val.value
            })))
            setTime(prevValue => prevValue.map(val => ({
                text: t("time." + val.value.replace(/[^A-Z0-9]/g, '_'), { ns: "display/components/calendar" }),
                value: val.value
            })))
            setSlotIntervals(prevValue => prevValue.map(val => ({
                text: t("slot_intervals." + val.value, { ns: "display/components/calendar" }),
                value: val.value
            })));
            setSlotDurations(prevValue => prevValue.map(val => ({
                text: t("slot_durations." + val.value.replace(/[^a-zA-Z0-9]/g, '_'), { ns: "display/components/calendar" }),
                value: val.value
            })));
            setTimeFormats([
                { text: t("time_hours.12_hours", { ns: "display/components/calendar" }), value: "12 hours" },
                { text: t("time_hours.24_hours", { ns: "display/components/calendar" }), value: "24 hours" }
            ])
            setWeekNumbers([
                {
                    "text": t("week_numbers.off", { ns: "display/components/calendar" }),
                    "value": "Off"
                },
                {
                    "text": t("week_numbers.first_day_of_year", { ns: "display/components/calendar" }),
                    "value": "First Day of Year"
                },
                {
                    "text": t("week_numbers.first_full_week", { ns: "display/components/calendar" }),
                    "value": "First Full Week"
                },
                {
                    "text": t("week_numbers.first_four_day_week", { ns: "display/components/calendar" }),
                    "value": "First Four-Day Week"
                }
            ])
        }
    }, [ready])

    // state
    const [tabSelected, setSelectedTab] = useState(0);

    // functions

    async function getData() {
        const attributes = await getAttributes(entityId);
        const attributesToStore = attributes.sort((a, b) => {
            if (a.isSystemAttribute && !b.isSystemAttribute) {
                return 1
            } else if (a.isSystemAttribute && b.isSystemAttribute) {
                return 0;
            } else {
                return -1
            }
        })
        setComponentPropertiesUsingAttributes(attributesToStore);
        updateGeneralFetchedValue("attributes", attributesToStore);
        setChangeProperty("isGeneralPropertiesChanged", false);
        setLoading(false);
    }

    function setComponentPropertiesUsingAttributes(attributes) {
        for (const property of formProperties) {
            switch (property) {
                case "startDateAttributeId": {
                    const attribute = attributes.find(attribute => attribute._id === selectedComponent.startDateAttributeId);
                    setProperties("startDateAttribute", attribute);
                    break;
                }
                case "endDateAttributeId": {
                    const attribute = attributes.find(attribute => attribute._id === selectedComponent.endDateAttributeId);
                    setProperties("endDateAttribute", attribute);
                    break;
                }
                case "flyoverLabelAttributeId": {
                    const attribute = attributes.find(attribute => attribute._id === selectedComponent.flyoverLabelAttributeId);
                    setProperties("flyoverLabelAttribute", attribute);
                    break;
                }
                case "addPageId": {
                    const page = portalPages.find(page => page._id === selectedComponent.addPageId);
                    setProperties("addPage", page);
                    break;
                }
                case "editPageId": {
                    const page = portalPages.find(page => page._id === selectedComponent.editPageId);
                    setProperties("editPage", page);
                    break;
                }
                default:
                    if (
                        selectedComponent[property] !== undefined
                    ) {
                        setProperties(property, selectedComponent[property])
                    }
                    break;
            }
        }
    }

    function handleTabSelect(event) {
        setSelectedTab(event.selected)
    }

    function handleInputChange(event) {
        switch (event.target.name) {
            case "defaultView":
            case "firstDayOfWeek":
            case "workStartHour":
            case "workEndHour":
            case "slotInterval":
            case "slotDuration":
            case "timeFormat":
            case "weekNumbers":
                setProperties(event.target.name, event.target.value.value);
                break;
            default:
                setProperties(event.target.name, event.target.value);
                break;
        }
    }

    function handleWorkWeeks(event) {
    }

    function filterDefaultView() {
        const defaultViews = DefaultViews.filter(view => {
            switch (view) {
                case "Month":
                    return properties.showMonth;
                case "Week":
                    return properties.showWeek;
                case "Day":
                    return properties.showDay;
                case "Year":
                    return properties.showYear;
                default:
                    return false;
            }
        })
        return defaultViews;
    }

    function shouldviewDisabled() {
        return false;
    }

    function setIsChanged() {
        let isChanged = false;
        for (const property of formProperties) {
            switch (property) {
                case "startDateAttributeId": {
                    if (
                        properties.startDateAttribute &&
                        selectedComponent.startDateAttributeId !== properties.startDateAttribute._id
                    ) {
                        isChanged = true;
                    }
                    break;
                }
                case "endDateAttributeId": {
                    if (
                        properties.endDateAttribute &&
                        selectedComponent.endDateAttributeId !== properties.endDateAttribute._id
                    ) {
                        isChanged = true;
                    }
                    break;
                }
                case "flyoverLabelAttributeId": {
                    if (
                        properties.flyoverLabelAttribute &&
                        selectedComponent.flyoverLabelAttributeId !== properties.flyoverLabelAttribute._id
                    ) {
                        isChanged = true;
                    }
                    break;
                }
                case "addPageId": {
                    if (
                        properties.addPage &&
                        selectedComponent.addPageId !== properties.addPage._id
                    ) {
                        isChanged = true;
                    }
                    break;
                }
                case "editPageId": {
                    if (
                        properties.editPage &&
                        selectedComponent.editPageId !== properties.editPage._id
                    ) {
                        isChanged = true;
                    }
                    break;
                }
                default:
                    if (selectedComponent[property] !== properties[property]) {
                        isChanged = true;
                    }
                    break;
            }
        }
        setChangeProperty("isGeneralPropertiesChanged", isChanged);
    }

    async function handleSave() {
        const updatedComponent = await saveComponent();
        updatePageComponent(selectedComponent.id, updatedComponent);
        if (isAttributeTableChanged && selectedComponent.componentsToSync && selectedComponent.componentsToSync.length > 0) {
            updateSyncedComponentAttributes();
        }
    }

    function resetComponent() {
        if (isGeneralPropertiesChanged) {
            setComponentPropertiesUsingAttributes(fetchedValues.attributes);
        }
        if (isAttributeTableChanged) {
            resetAttributes();
        }
        if (isActionTableChanged) {
            resetActions();
        }
    }

    function filteredFlyoverLabelAttribute() {
        const filteredAttributes = fetchedValues.attributes.filter(attribute =>
            attribute.attributeType !== "Html" &&
            attribute.attributeType !== "Image" &&
            !(attribute.attributeType === "Mirror" &&
                (attribute.attribute.attributeType === "Html" || attribute.attribute.attributeType === "Image"))
        );

        return filteredAttributes;
    }

    if (loading) {
        return (
            <ComponentFormSkeleton />
        )
    }

    function getValueFromArray(array, value) {
        return array.find(val => val.value === value);
    }

    return (
        <div className={"h-100 d-flex flex-column line" + (!accessedFromDesigner && " runtimePopup")}>
            <ComponentToolbar onSave={handleSave} onReset={resetComponent} />
            <Scrollable noPadding={true}>
                <TabStrip className="flex-grow-1 h-100 edit1" selected={tabSelected} onSelect={handleTabSelect}>
                    <TabStripTab title={t("tabs.general", { ns: "display/common" })}>
                        <Scrollable>
                            <form className="p-2 edit2">
                                {accessedFromDesigner && (
                                    <>
                                        <div className="row">
                                            <div className="col-3">
                                                <Checkbox
                                                    name="showGridlines"
                                                    disabled={selectedComponent.hidden}
                                                    checked={properties.showGridlines}
                                                    onChange={handleInputChange}
                                                    label={t("form_labels.show_gridlines", { ns: "display/components/calendar" })}
                                                    className="me-2"
                                                />
                                            </div>
                                            <div className="col-3">
                                                <Checkbox
                                                    name="allowExport"
                                                    disabled={selectedComponent.hidden}
                                                    checked={properties.allowExport}
                                                    onChange={handleInputChange}
                                                    label={t("form_labels.allow_export", { ns: "display/components/calendar" })}
                                                    className="me-2"
                                                />
                                            </div>
                                            <div className="col-3">
                                                <Checkbox
                                                    name="showPrint"
                                                    disabled={selectedComponent.hidden}
                                                    checked={properties.showPrint}
                                                    onChange={handleInputChange}
                                                    label={t("form_labels.show_print", { ns: "display/components/calendar" })}
                                                    className="me-2"
                                                />
                                            </div>
                                            <div className="col-3">
                                                <Checkbox
                                                    name="showTooltip"
                                                    disabled={selectedComponent.hidden}
                                                    checked={properties.showTooltip}
                                                    onChange={handleInputChange}
                                                    label={t("form_labels.show_tooltip", { ns: "display/components/calendar" })}
                                                    className="me-2"
                                                />
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-3">
                                                <Checkbox
                                                    name="showDay"
                                                    disabled={selectedComponent.hidden || (shouldviewDisabled() && properties.showDay)}
                                                    checked={properties.showDay}
                                                    onChange={handleInputChange}
                                                    label={t("form_labels.show_day", { ns: "display/components/calendar" })}
                                                    className="me-2"
                                                />
                                            </div>
                                            <div className="col-3">
                                                <Checkbox
                                                    name="showWeek"
                                                    disabled={selectedComponent.hidden || (shouldviewDisabled() && properties.showWeek)}
                                                    checked={properties.showWeek}
                                                    onChange={handleInputChange}
                                                    label={t("form_labels.show_week", { ns: "display/components/calendar" })}
                                                    className="me-2"
                                                />
                                            </div>
                                            <div className="col-3">
                                                <Checkbox
                                                    name="showMonth"
                                                    disabled={selectedComponent.hidden || (shouldviewDisabled() && properties.showMonth)}
                                                    checked={properties.showMonth}
                                                    onChange={handleInputChange}
                                                    label={t("form_labels.show_month", { ns: "display/components/calendar" })}
                                                    className="me-2"
                                                />
                                            </div>
                                            <div className="col-3">
                                                <Checkbox
                                                    name="showYear"
                                                    disabled={selectedComponent.hidden || (shouldviewDisabled() && properties.showYear)}
                                                    checked={properties.showYear}
                                                    onChange={handleInputChange}
                                                    label={t("form_labels.show_year", { ns: "display/components/calendar" })}
                                                    className="me-2"
                                                />
                                            </div>
                                        </div>

                                        <div className="form-label">{t("form_labels.default_view", { ns: "display/components/calendar" })}</div>
                                        <DropDownList
                                            name="defaultView"
                                            disabled={selectedComponent.hidden || shouldviewDisabled()}
                                            onChange={handleInputChange}
                                            value={getValueFromArray(DefaultViews, properties.defaultView)}
                                            data={filterDefaultView()}
                                            textField="text"
                                            dataItemKey="value"
                                        />

                                        <Checkbox
                                            name="enableCalendarSettings"
                                            disabled={selectedComponent.hidden}
                                            checked={properties.enableCalendarSettings}
                                            onChange={handleInputChange}
                                            label={t("form_labels.enable_calendar_settings", { ns: "display/components/calendar" })}
                                            className="me-2 d-block"
                                        />

                                        {properties.enableCalendarSettings && (
                                            <>
                                                <div className="row">
                                                    <div className="col-3">
                                                        <div className="form-label">{t("form_labels.first_day_of_week", { ns: "display/components/calendar" })}</div>
                                                        <DropDownList
                                                            name="firstDayOfWeek"
                                                            disabled={selectedComponent.hidden}
                                                            onChange={handleInputChange}
                                                            value={getValueFromArray(Weeks, properties.firstDayOfWeek)}
                                                            data={Weeks}
                                                            textField="text"
                                                            dataItemKey="value"
                                                        />
                                                    </div>
                                                    <div className="col-3">
                                                        <div className="form-label">
                                                            {t("form_labels.work_start_hour", { ns: "display/components/calendar" })}
                                                        </div>
                                                        <DropDownList
                                                            name="workStartHour"
                                                            disabled={selectedComponent.hidden}
                                                            onChange={handleInputChange}
                                                            value={getValueFromArray(Time, properties.workStartHour)}
                                                            data={Time}
                                                            textField="text"
                                                            dataItemKey="value"
                                                        />
                                                    </div>
                                                    <div className="col-3">
                                                        <div className="form-label">
                                                            {t("form_labels.work_end_hour", { ns: "display/components/calendar" })}
                                                        </div>
                                                        <DropDownList
                                                            name="workEndHour"
                                                            disabled={selectedComponent.hidden}
                                                            onChange={handleInputChange}
                                                            value={getValueFromArray(Time, properties.workEndHour)}
                                                            data={Time}
                                                            textField="text"
                                                            dataItemKey="value"
                                                        />
                                                    </div>
                                                    <div className="col-3">
                                                        <div className="form-label">
                                                            {t("form_labels.slot_interval", { ns: "display/components/calendar" })}
                                                        </div>
                                                        <DropDownList
                                                            name="slotInterval"
                                                            disabled={selectedComponent.hidden}
                                                            onChange={handleInputChange}
                                                            value={getValueFromArray(slotIntervals, properties.slotInterval)}
                                                            data={slotIntervals}
                                                            textField="text"
                                                            dataItemKey="value"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="mt-1 row">
                                                    <div className="col-3">
                                                        <div className="form-label">
                                                            {t("form_labels.slot_duration", { ns: "display/components/calendar" })}
                                                        </div>
                                                        <DropDownList
                                                            name="slotDuration"
                                                            disabled={selectedComponent.hidden}
                                                            onChange={handleInputChange}
                                                            value={getValueFromArray(slotDurations, properties.slotDuration)}
                                                            data={slotDurations}
                                                            textField="text"
                                                            dataItemKey="value"
                                                        />
                                                    </div>
                                                    <div className="col-3">
                                                        <div className="form-label">
                                                            {t("form_labels.time_format", { ns: "display/components/calendar" })}
                                                        </div>
                                                        <DropDownList
                                                            name="timeFormat"
                                                            disabled={selectedComponent.hidden}
                                                            onChange={handleInputChange}
                                                            value={getValueFromArray(TimeFormats, properties.timeFormat)}
                                                            data={TimeFormats}
                                                            textField="text"
                                                            dataItemKey="value"
                                                        />
                                                    </div>
                                                    <div className="col-3">
                                                        <div className="form-label">
                                                            {t("form_labels.week_numbers", { ns: "display/components/calendar" })}
                                                        </div>
                                                        <DropDownList
                                                            name="weekNumbers"
                                                            disabled={selectedComponent.hidden}
                                                            onChange={handleInputChange}
                                                            value={getValueFromArray(WeekNumbers, properties.weekNumbers)}
                                                            data={WeekNumbers}
                                                            textField="text"
                                                            dataItemKey="value"
                                                        />
                                                    </div>
                                                    <div className="col-3">
                                                        <div className="form-label">
                                                            {t("form_labels.work_weeks", { ns: "display/components/calendar" })}
                                                        </div>
                                                        <WorkWeekMultiSelect disabled={selectedComponent.hidden} />
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                        <div className="my-3 border-top"></div>
                                    </>
                                )}
                                <div className="d-block mb-3">
                                    <h6>
                                        {t("form_labels.calendar_mapping_properties", { ns: "display/components/calendar" })}
                                    </h6>
                                    <MappingAttributeForm />
                                </div>
                                {accessedFromDesigner && (
                                    <>
                                        <div className="my-3 border-top"></div>
                                        <div className="d-block">
                                            <h6>
                                                {t("form_labels.event_actions.label", { ns: "display/components/calendar" })}
                                            </h6>
                                            <EventActions />
                                        </div>
                                    </>
                                )}
                                <Checkbox
                                    name="useAttributeInFlyoverLabel"
                                    disabled={selectedComponent.hidden}
                                    checked={properties.useAttributeInFlyoverLabel}
                                    onChange={handleInputChange}
                                    label={t("form_labels.use_property_in_label", { ns: "display/components/calendar" })}
                                />

                                {(properties.useAttributeInFlyoverLabel || selectedComponent.hidden) && (
                                    <>
                                        <div className="form-label">{t("form_labels.label_property", { ns: "display/components/calendar" })}</div>
                                        <DropDownList
                                            name="flyoverLabelAttribute"
                                            disabled={!properties.useAttributeInFlyoverLabel || selectedComponent.hidden}
                                            onChange={handleInputChange}
                                            value={properties.flyoverLabelAttribute}
                                            data={filteredFlyoverLabelAttribute()}
                                            textField="attributeName"
                                            dataItemKey="_id"
                                            className="w-25"
                                        />
                                    </>
                                )}
                                {!(properties.useAttributeInFlyoverLabel || selectedComponent.hidden) && (
                                    <>
                                        <div className="form-label">{t("form_labels.label", { ns: "display/components/calendar" })}</div>
                                        <input
                                            name="flyoverLabel"
                                            className="form-control w-25"
                                            onChange={handleInputChange}
                                            disabled={properties.useAttributeInFlyoverLabel || selectedComponent.hidden}
                                            value={properties.flyoverLabel}
                                        />
                                    </>
                                )}
                            </form>
                        </Scrollable>
                    </TabStripTab>
                    <TabStripTab title={t("tabs.properties", { ns: "display/common" })}>
                        <AttributeTable />
                    </TabStripTab>
                    <TabStripTab title={t("tabs.actions", { ns: "display/common" })}>
                        <ActionTable />
                    </TabStripTab>
                </TabStrip>
            </Scrollable>
        </div>
    )
}

export default Calendar;